import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { StrategicPillarService, StrategicPillar, EntityType } from '../../services/strategic-pillar.service';
import { RdsFormFieldModule, RdsMultiAutocompleteComponent } from '@rds/angular-components';

@Component({
  selector: 'app-strategic-pillar-selector',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RdsFormFieldModule,
    RdsMultiAutocompleteComponent
],
  template: `
    <div class="strategic-pillar-selector">
      @if (isViewer) {
        <!-- Viewer: Simple list only -->
        <div class="list-content">
          @if (selectedPillars.length === 0) {
            <rds-form-field labelPosition="side-left">
              <input rdsInput [value]="'None'" readonly disabled />
            </rds-form-field>
          }
          @for (pillar of selectedPillars; track pillar.id) {
            <rds-form-field labelPosition="side-left">
              <input rdsInput [value]="pillar.title" readonly disabled />
            </rds-form-field>
          }
        </div>
      } @else {
        <!-- Editor: Dropdown + list below -->
        <div class="editor-content">
          <rds-form-field id="strategic-pillar-dropdown">
            <rds-multi-autocomplete 
              [formControl]="pillarSelectControl"
              [options]="formattedPillars"
              placeholder="Select…"
              [highlightQuery]="false"
              minSearchChars="0"
              debounce="0"
              displayProperty="title"
              uniqProperty="id">
            </rds-multi-autocomplete>
          </rds-form-field>
          @if (selectedPillars.length > 0) {
            <div class="selected-list">
              @for (pillar of selectedPillars; track pillar.id) {
                <rds-form-field labelPosition="side-left">
                  <input rdsInput [value]="pillar.title" readonly />
                </rds-form-field>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .strategic-pillar-selector {
      .list-content,
      .selected-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      
      .editor-content {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      
      ::ng-deep #strategic-pillar-dropdown {
        rds-multi-autocomplete {
          .rds-chips__chip {
            visibility: hidden !important;
            position: absolute !important;
            width: 0 !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
        }
      }
      
      /* Hide any duplicate labels when used in pillars-audiences-section */
      :host-context(app-pillars-audiences-section) {
        .section-label,
        .field-label,
        h3, h4, h5,
        .ui-heading,
        [data-label] {
          display: none !important;
        }
      }
    }
  `]
})
export class StrategicPillarSelectorComponent implements OnInit, OnDestroy {
  @Input() entityType!: EntityType;
  @Input() entityId!: string;
  @Input() isViewer: boolean = false;
  @Input() initialPillars: StrategicPillar[] = []; // Receive initial pillars from parent
  @Output() pillarChanged = new EventEmitter<{ pillarIds: number[], pillars: StrategicPillar[] }>();
  @ViewChild(RdsMultiAutocompleteComponent) multiAutocomplete!: RdsMultiAutocompleteComponent<StrategicPillar>;

  pillarSelectControl = new FormControl<StrategicPillar[] | null>(null);
  allPillars: StrategicPillar[] = [];
  formattedPillars: StrategicPillar[] = [];
  selectedPillars: StrategicPillar[] = [];
  originalPillarIds: number[] = [];
  hasPendingChanges: boolean = false;

  private subscriptions: Subscription[] = [];

  constructor(private strategicPillarService: StrategicPillarService, private elementRef: ElementRef) {}

  ngOnInit() {
    this.loadData();
    this.setupPillarSelection();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadData() {
    const allPillarsSub = this.strategicPillarService.getAllPillars().subscribe({
      next: (pillars) => {
        this.allPillars = pillars;
        this.formattedPillars = pillars;
        
        // Use initial pillars from parent instead of fetching from /tagged endpoint
        if (this.initialPillars && this.initialPillars.length > 0) {
          this.selectedPillars = this.initialPillars;
          this.originalPillarIds = this.initialPillars.map(p => p.id);
          this.pillarSelectControl.setValue(this.initialPillars);
        }
      },
      error: (err) => console.error('Error loading strategic pillars:', err)
    });
    this.subscriptions.push(allPillarsSub);
  }

  private setupPillarSelection() {
    const selectionSub = this.pillarSelectControl.valueChanges.subscribe(selectedPillars => {
      // Prevent viewers from making changes
      if (this.isViewer) {
        return;
      }
      
      const currentPillars = selectedPillars || [];
      const currentIds = currentPillars.map(p => p.id);
      
      // Always update the display
      this.selectedPillars = currentPillars;
      
      const hasChanged = JSON.stringify(currentIds.sort()) !== JSON.stringify(this.originalPillarIds.sort());
      
      if (hasChanged) {
        this.hasPendingChanges = true;
        this.pillarChanged.emit({ 
          pillarIds: currentIds, 
          pillars: currentPillars 
        });
      }
    });
    this.subscriptions.push(selectionSub);
  }

  getCurrentSelection(): { pillarIds: number[], pillars: StrategicPillar[] } {
    return {
      pillarIds: this.selectedPillars.map(p => p.id),
      pillars: this.selectedPillars
    };
  }

  markAsSaved() {
    this.originalPillarIds = this.selectedPillars.map(p => p.id);
    this.hasPendingChanges = false;
  }

  reset() {
    const formattedSelected = this.originalPillarIds
      .map(id => this.formattedPillars.find(p => p.id === id))
      .filter(p => p !== undefined) as StrategicPillar[];
    this.selectedPillars = formattedSelected;
    this.pillarSelectControl.setValue(formattedSelected);
    this.hasPendingChanges = false;
  }

  removePillar(pillarToRemove: StrategicPillar) {
    // Prevent viewers from removing pillars
    if (this.isViewer) {
      return;
    }
    
    this.selectedPillars = this.selectedPillars.filter(p => p.id !== pillarToRemove.id);
    this.pillarSelectControl.setValue(this.selectedPillars);
    this.hasPendingChanges = JSON.stringify(this.selectedPillars.map(p => p.id).sort()) !== JSON.stringify(this.originalPillarIds.sort());
    
    this.pillarChanged.emit({ 
      pillarIds: this.selectedPillars.map(p => p.id), 
      pillars: this.selectedPillars 
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Check if click is inside this component's autocomplete or overlay
    const clickedInsideComponent = this.elementRef.nativeElement.contains(target);
    const clickedInsideOverlay = target.closest('.rds-autocomplete-panel') || 
                                  target.closest('.cdk-overlay-container');
    if (!clickedInsideComponent && !clickedInsideOverlay) {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    this.multiAutocomplete?.hide();
  }
}
